import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, ShieldAlert, Calendar, Database, X, ChevronDown, Check, AlertCircle, Lock } from 'lucide-react';
import { locationTypes, getLocationOptions, handleLocationSelect, getStateAbbreviation, getCountiesForState, getCitiesForState } from '../../constants/locations';
import './Filters.css';

const Filters = ({ 
  isFilterOpen, 
  onClose, 
  isMobileLayout, 
  isPinned, 
  setIsPinned, 
  selectedLocation,
  onLocationSelect,
  selectedCrime,
  setSelectedCrime,
  selectedDateRange,
  setSelectedDateRange,
  selectedSource,
  setSelectedSource,
  availableYears,
  onMenuStateChange,
  onSaveSearch,
  userPlan = 'free',
  mostRecentYear = '2024'
}) => {
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isSideCrimeTypeOpen, setIsSideCrimeTypeOpen] = useState(false);
  const [isSideDateRangeOpen, setIsSideDateRangeOpen] = useState(false);
  const [isSideDataSourceOpen, setIsSideDataSourceOpen] = useState(false);
  const [isMainCrimeTypeOpen, setIsMainCrimeTypeOpen] = useState(false);
  const [isMainDateRangeOpen, setIsMainDateRangeOpen] = useState(false);
  const [isMainDataSourceOpen, setIsMainDataSourceOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [openStateSubmenu, setOpenStateSubmenu] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 360);
  const [saveState, setSaveState] = useState('idle');
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(saveTimeoutRef.current);
  }, []);

  const locationButtonRef = useRef(null);
  const crimeButtonRef = useRef(null);
  const dateButtonRef = useRef(null);
  const sourceButtonRef = useRef(null);

  useEffect(() => {
    const handleGlobalClick = (event) => {
      const isClickInPortal = document.querySelector('.filter-panel-dropdown')?.contains(event.target);
      const isClickInLocationPortal = document.querySelector('.location-dropdown-portal')?.contains(event.target);
      
      const isInOurDropdowns = 
        event.target.closest('.custom-dropdown') && 
        (event.target.closest('.filter-menu') || event.target.closest('.filter-panel'));
      
      if (!isInOurDropdowns && !isClickInPortal && !isClickInLocationPortal) {
        setIsLocationOpen(false);
        setIsSideCrimeTypeOpen(false);
        setIsSideDateRangeOpen(false);
        setIsSideDataSourceOpen(false);
        setIsMainCrimeTypeOpen(false);
        setIsMainDateRangeOpen(false);
        setIsMainDataSourceOpen(false);
        setOpenSubmenu(null);
        setOpenStateSubmenu(null);
      }
    };
  
    document.addEventListener('mousedown', handleGlobalClick);
    return () => {
      document.removeEventListener('mousedown', handleGlobalClick);
    };
  }, []);

  useEffect(() => {
    const isAnyDropdownOpen = isLocationOpen || isSideCrimeTypeOpen || isSideDateRangeOpen || 
      isSideDataSourceOpen || isMainCrimeTypeOpen || isMainDateRangeOpen || isMainDataSourceOpen;

    if (onMenuStateChange) {
      onMenuStateChange(isAnyDropdownOpen);
    }
      
    if (isAnyDropdownOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isLocationOpen, isSideCrimeTypeOpen, isSideDateRangeOpen, isSideDataSourceOpen, 
      isMainCrimeTypeOpen, isMainDateRangeOpen, isMainDataSourceOpen, onMenuStateChange]);

  useEffect(() => {
    if (selectedSource === 'BJS_NCVS' && selectedCrime === 'murder') {
      setSelectedCrime('all_crime');
    }
    if (selectedSource !== 'BJS_NCVS' && 
        (selectedCrime === 'simple_assault' || selectedCrime === 'trespassing')) {
      setSelectedCrime('all_crime');
    }
  }, [selectedSource, selectedCrime, setSelectedCrime]);
  
  useEffect(() => {
    if (selectedLocation?.info?.type !== 'nation' && selectedSource === 'BJS_NCVS') {
      setSelectedSource('FBI_CIUS');
    }
  }, [selectedLocation?.info?.type, selectedSource, setSelectedSource]);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 360);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handlePinToggle = (e) => {
    setIsPinned(e.target.checked ? 'pinned' : 'unpinned');
  };

  const handleSaveClick = useCallback(async () => {
    if (saveState !== 'idle' || !onSaveSearch) return;
    setSaveState('saving');
    const result = await onSaveSearch();
    if (result === 'saved' || result === 'duplicate') {
      setSaveState(result);
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => setSaveState('idle'), 2500);
    } else {
      setSaveState('idle');
    }
  }, [saveState, onSaveSearch]);

  const closeAllSideMenus = () => {
    setIsLocationOpen(false);
    setIsSideCrimeTypeOpen(false);
    setIsSideDateRangeOpen(false);
    setIsSideDataSourceOpen(false);
    setOpenSubmenu(null);
    setOpenStateSubmenu(null);
  };

  const handleFilterLocationSelect = (location, type) => {
    const isAggregateSelection = location.label && (
      location.label.includes('All ') || 
      location.isTypeView || 
      location.isAllStateCounties || 
      location.isAllStateCities
    );
    
    if (!isAggregateSelection) {
      const locationWithInfo = {
        ...location,
        info: {
          ...location.info,
          fromHeader: true
        }
      };
      handleLocationSelect(locationWithInfo, type, onLocationSelect, closeAllSideMenus);
    } else {
      handleLocationSelect(location, type, onLocationSelect, closeAllSideMenus);
    }
  };

  const getCrimeTypes = (source) => {
    if (source === 'BJS_NCVS') {
      return [
        { value: 'all_crime', label: 'All Crime' },
        { value: 'violent_crime', label: 'Violent Crime', subTypes: [
          { value: 'rape', label: 'Rape' },
          { value: 'robbery', label: 'Robbery' },
          { value: 'aggravated_assault', label: 'Aggravated Assault' },
          { value: 'simple_assault', label: 'Simple Assault' }
        ]},
        { value: 'property_crime', label: 'Property Crime', subTypes: [
          { value: 'burglary', label: 'Burglary' },
          { value: 'trespassing', label: 'Trespassing' },
          { value: 'larceny_theft', label: 'Larceny/Theft' },
          { value: 'motor_vehicle_theft', label: 'Motor Vehicle Theft' }
        ]}
      ];
    }
  
    return [
      { value: 'all_crime', label: 'All Crime' },
      { value: 'violent_crime', label: 'Violent Crime', subTypes: [
        { value: 'murder', label: 'Murder' },
        { value: 'rape', label: 'Rape' },
        { value: 'robbery', label: 'Robbery' },
        { value: 'aggravated_assault', label: 'Aggravated Assault' }
      ]},
      { value: 'property_crime', label: 'Property Crime', subTypes: [
        { value: 'burglary', label: 'Burglary' },
        { value: 'larceny_theft', label: 'Larceny/Theft' },
        { value: 'motor_vehicle_theft', label: 'Motor Vehicle Theft' }
      ]}
    ];
  };

  const getSelectedCrimeLabel = () => {
    const allCrimeTypes = getCrimeTypes(selectedSource);
    
    for (const type of allCrimeTypes) {
      if (type.value === selectedCrime) {
        return type.label;
      }
      
      if (type.subTypes) {
        const subType = type.subTypes.find(sub => sub.value === selectedCrime);
        if (subType) {
          return subType.label;
        }
      }
    }
    
    return 'All Crime';
  };

  const handleCrimeTypeSelect = (crimeType, fromSideMenu = false) => {
    setSelectedCrime(crimeType);
    if (fromSideMenu) {
      setIsSideCrimeTypeOpen(false);
    } else {
      setIsMainCrimeTypeOpen(false);
    }
  };

  const getDateRanges = () => {
    if (availableYears && availableYears.length > 0) {
      const sortedYears = [...availableYears].sort((a, b) => parseInt(b) - parseInt(a));
      
      return sortedYears.map(year => ({
        value: year.toString(),
        label: year.toString()
      }));
    }
    
    if (selectedSource === 'FBI_CIUS') {
      return Array.from({ length: 26 }, (_, i) => ({
        value: (2024 - i).toString(),
        label: (2024 - i).toString()
      }));
    } else if (selectedSource === 'BJS_NCVS') {
      return Array.from({ length: 31 }, (_, i) => ({
        value: (2024 - i).toString(),
        label: (2024 - i).toString()
      }));
    } else {
      return [
        { value: 'last_30_days', label: 'Last 30 Days' },
        { value: 'last_90_days', label: 'Last 90 Days' },
        { value: 'last_6_months', label: 'Last 6 Months' },
        { value: 'last_year', label: 'Last Year' },
        { value: 'custom', label: 'Custom Range' }
      ];
    }
  };

  const dateRanges = getDateRanges();

  const isYearLockedForFreePlan = (rangeValue) => {
    if (userPlan !== 'free') return false;
    if (!/^\d{4}$/.test(String(rangeValue))) return false;
    return String(rangeValue) !== String(mostRecentYear);
  };

  useEffect(() => {
    if (availableYears && availableYears.length > 0) {
      const yearExists = availableYears.some(year => year.toString() === selectedDateRange);
      
      if (!yearExists && !selectedLocation?.info?.skipResolutionCheck) {
        const sortedYears = [...availableYears].sort((a, b) => parseInt(b) - parseInt(a));
        setSelectedDateRange(sortedYears[0].toString());
      }
    }
  }, [availableYears, selectedDateRange, setSelectedDateRange, selectedLocation?.info?.skipResolutionCheck]);

  const getDataSources = (locationType) => {
    if (locationType === 'nation') {
      return [
        { value: 'FBI_CIUS', label: 'FBI (CIUS)' },
        { value: 'BJS_NCVS', label: 'BJS (NCVS)' }
      ];
    } else if (locationType === 'county' || locationType === 'city') {
      return [
        { value: 'FBI_CIUS', label: 'FBI (CIUS)' },
      ];
    } else {
      return [
        { value: 'FBI_CIUS', label: 'FBI (CIUS)' },
      ];
    }
  };

  const formatLocationName = (locationInfo) => {
    if (!locationInfo?.name) return 'Select Location';
    
    if (locationInfo.type === 'county' && locationInfo.name.includes(', ')) {
      const parts = locationInfo.name.split(', ');
      const county = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      const stateAbbr = getStateAbbreviation(state.trim());
      return `${county}, ${stateAbbr}`;
    }
    
    if (locationInfo.type === 'city' && locationInfo.name.includes(', ')) {
      const parts = locationInfo.name.split(', ');
      const city = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      const stateAbbr = getStateAbbreviation(state.trim());
      return `${city}, ${stateAbbr}`;
    }
    
    return locationInfo.name;
  };

  const filterDropdowns = (
    <div className="filter-dropdowns">
      <div className="filter-select-container">
        <div className="filter-select-label">
          <div className="top-row">
            <ShieldAlert size={16} />
            <span>Crime Type:</span>
          </div>
        </div>
        <div className={`custom-dropdown crime-dropdown ${isMainCrimeTypeOpen ? 'open' : ''}`}>
          <button 
            className="filter-select"
            ref={crimeButtonRef}
            onClick={(e) => {
              e.stopPropagation();
              setIsMainCrimeTypeOpen(!isMainCrimeTypeOpen);
              setIsMainDateRangeOpen(false);
              setIsMainDataSourceOpen(false);
              setIsLocationOpen(false);
              setIsSideCrimeTypeOpen(false);
              setIsSideDateRangeOpen(false);
              setIsSideDataSourceOpen(false);
              setOpenSubmenu(null);
              setOpenStateSubmenu(null);
            }}
          >
            {getSelectedCrimeLabel()}
          </button>
          {isMainCrimeTypeOpen && createPortal(
            <div 
              className="dropdown-menu filter-panel-dropdown" 
              style={{
                position: 'absolute',
                top: `${crimeButtonRef.current.getBoundingClientRect().bottom + window.scrollY}px`,
                left: `${crimeButtonRef.current.getBoundingClientRect().left + window.scrollX}px`,
                minWidth: `${crimeButtonRef.current.getBoundingClientRect().width}px`,
                width: 'max-content',
                maxWidth: 'max-content',
                zIndex: 9999
              }}
            >
              <div className="menu-section">
                <div 
                  key="all_crime"
                  className={`menu-item ${selectedCrime === 'all_crime' ? 'disabled' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectedCrime !== 'all_crime') {
                      handleCrimeTypeSelect('all_crime', false);
                    }
                  }}
                >
                  All Crime
                </div>
              
                {getCrimeTypes(selectedSource).filter(type => type.value !== 'all_crime').map((type, index) => (
                  <div key={type.value}>
                    <div className="submenu">
                      <div 
                        className={`menu-item ${selectedCrime === type.value ? 'disabled' : ''}`}
                        style={{ 
                          borderTop: index > 0 ? '1px solid var(--border-color)' : 'none' 
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selectedCrime !== type.value) {
                            handleCrimeTypeSelect(type.value, false);
                          }
                        }}
                      >
                        {type.label}
                      </div>
                    
                      {type.subTypes && (
                        <div className="submenu">
                          {type.subTypes.map(subType => (
                            <div 
                              key={subType.value} 
                              className={`menu-item ${selectedCrime === subType.value ? 'disabled' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (selectedCrime !== subType.value) {
                                  handleCrimeTypeSelect(subType.value, false);
                                }
                              }}
                            >
                              {subType.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>

      <div className="filter-select-container">
        <div className="filter-select-label">
          <div className="top-row">
            <Calendar size={16} />
            <span>Date Range:</span>
          </div>
        </div>
        <div className={`custom-dropdown date-dropdown ${isMainDateRangeOpen ? 'open' : ''}`}>
          <button 
            className="filter-select"
            ref={dateButtonRef}
            onClick={(e) => {
              e.stopPropagation();
              setIsMainDateRangeOpen(!isMainDateRangeOpen);
              setIsMainCrimeTypeOpen(false);
              setIsMainDataSourceOpen(false);
              setIsLocationOpen(false);
              setIsSideCrimeTypeOpen(false);
              setIsSideDateRangeOpen(false);
              setIsSideDataSourceOpen(false);
              setOpenSubmenu(null);
              setOpenStateSubmenu(null);
            }}
          >
            {dateRanges.find(range => range.value === String(selectedDateRange))?.label || dateRanges[0].label}
          </button>
          {isMainDateRangeOpen && createPortal(
            <div 
              className="dropdown-menu filter-panel-dropdown" 
              style={{
                position: 'absolute',
                top: `${dateButtonRef.current.getBoundingClientRect().bottom + window.scrollY}px`,
                left: `${dateButtonRef.current.getBoundingClientRect().left + window.scrollX}px`,
                minWidth: `${dateButtonRef.current.getBoundingClientRect().width}px`,
                width: 'max-content',
                maxWidth: 'max-content',
                maxHeight: window.innerWidth < 768 ? '320px' : '335px',
                overflowY: 'auto',
                zIndex: 9999
              }}
            >
              {dateRanges.map(range => {
                const yearLocked = isYearLockedForFreePlan(range.value);
                const isSelected = String(selectedDateRange) === range.value;
                return (
                  <div
                    key={range.value}
                    className={`menu-item ${isSelected ? 'disabled' : ''}${yearLocked ? ' year-locked-free' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isSelected) {
                        setSelectedDateRange(range.value);
                        setIsMainDateRangeOpen(false);
                      }
                    }}
                  >
                    <span className="filter-year-menu-text">{range.label}</span>
                    {yearLocked && (
                      <Lock size={14} strokeWidth={2} className="filter-year-lock-icon" aria-hidden />
                    )}
                  </div>
                );
              })}
            </div>,
            document.body
          )}
        </div>
      </div>

      <div className="filter-select-container">
        <div className="filter-select-label">
          <div className="top-row">
            <Database size={16} />
            <span>{!isSmallScreen ? 'Data Source:' : 'Source:'}</span>
          </div>
        </div>
        <div className={`custom-dropdown source-dropdown ${isMainDataSourceOpen ? 'open' : ''}`}>
          <button 
            className="filter-select"
            ref={sourceButtonRef}
            onClick={(e) => {
              e.stopPropagation();
              setIsMainDataSourceOpen(!isMainDataSourceOpen);
              setIsMainCrimeTypeOpen(false);
              setIsMainDateRangeOpen(false);
              setIsLocationOpen(false);
              setIsSideCrimeTypeOpen(false);
              setIsSideDateRangeOpen(false);
              setIsSideDataSourceOpen(false);
              setOpenSubmenu(null);
              setOpenStateSubmenu(null);
            }}
          >
            {getDataSources(selectedLocation?.info?.type).find(source => source.value === selectedSource)?.label || getDataSources(selectedLocation?.info?.type)[0].label}
          </button>
          {isMainDataSourceOpen && createPortal(
            <div 
              className="dropdown-menu filter-panel-dropdown" 
              style={{
                position: 'absolute',
                top: `${sourceButtonRef.current.getBoundingClientRect().bottom + window.scrollY}px`,
                left: `${sourceButtonRef.current.getBoundingClientRect().left + window.scrollX}px`,
                minWidth: `${sourceButtonRef.current.getBoundingClientRect().width}px`,
                width: 'max-content',
                maxWidth: 'max-content',
                zIndex: 9999
              }}
            >
              {getDataSources(selectedLocation?.info?.type).map(source => (
                <div 
                  key={source.value} 
                  className={`menu-item ${selectedSource === source.value ? 'disabled' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectedSource !== source.value) {
                      setSelectedSource(source.value);
                      setIsMainDataSourceOpen(false);
                    }
                  }}
                >
                  {source.label}
                </div>
              ))}
            </div>,
            document.body
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isPinned === 'pinned' && (
        <div className="filter-panel" data-pinned={isPinned}>
          <div className={`filter-grid ${isMobileLayout ? 'mobile' : 'desktop'}`}>
            {filterDropdowns}
            <button
              className={`filter-button save ${isMobileLayout ? 'mobile' : 'desktop'} ${saveState === 'saved' ? 'saved' : saveState === 'duplicate' ? 'duplicate' : saveState === 'saving' ? 'saving' : ''}`}
              onClick={handleSaveClick}
              disabled={saveState === 'saving' || !selectedLocation?.info?.name}
            >
              {saveState === 'saved' ? (
                <div className="text-container">
                  <span>Search</span>
                  <span>Saved!</span>
                </div>
              ) : saveState === 'duplicate' ? (
                <div className="text-container">
                  <span>Already</span>
                  <span>Saved</span>
                </div>
              ) : saveState === 'saving' ? (
                <div className="text-container">
                  <span>Saving</span>
                  <span>...</span>
                </div>
              ) : (
                <div className="text-container">
                  <span>Save</span>
                  <span>Search</span>
                </div>
              )}
            </button>
          </div>
        </div>
      )}

      {isFilterOpen && createPortal(
        <>
          <div className={`filter-menu-overlay ${isFilterOpen ? 'active' : ''}`} onClick={onClose}></div>
          <div className={`filter-menu ${isMobileLayout ? 'mobile' : 'desktop'} ${isFilterOpen ? 'active' : ''}`}>
            <div className="filter-header">
              <h2>Filters</h2>
              <button className="filter-close" onClick={onClose}>
                <X size={24} />
              </button>
            </div>

            <div className="filter-content">
              <div className="filter-grid">
                <div className="filter-dropdowns">
                  <div className="filter-select-container">
                    <div className="filter-select-label">
                      <div className="top-row">
                        <MapPin size={16} />
                        <span>Location:</span>
                      </div>
                    </div>
                    <div className={`custom-dropdown location-dropdown ${isLocationOpen ? 'open' : ''}`}>
                      <button 
                        className="dropdown-trigger"
                        ref={locationButtonRef}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsLocationOpen(!isLocationOpen);
                          setIsSideCrimeTypeOpen(false);
                          setIsSideDateRangeOpen(false);
                          setIsSideDataSourceOpen(false);
                          setIsMainCrimeTypeOpen(false);
                          setIsMainDateRangeOpen(false);
                          setIsMainDataSourceOpen(false);
                        }}
                      >
                        {formatLocationName(selectedLocation.info)}
                      </button>
                      {isLocationOpen && locationButtonRef.current && createPortal(
                        <div 
                          className="dropdown-menu location-dropdown-portal" 
                          style={{
                            position: 'absolute',
                            top: `${locationButtonRef.current.getBoundingClientRect().bottom + window.scrollY}px`,
                            left: `${locationButtonRef.current.getBoundingClientRect().left + (locationButtonRef.current.getBoundingClientRect().width / 2) + window.scrollX}px`,
                            transform: 'translateX(-50%)',
                            width: `${locationButtonRef.current.getBoundingClientRect().width}px`,
                            maxWidth: `${locationButtonRef.current.getBoundingClientRect().width}px`,
                            minWidth: `${locationButtonRef.current.getBoundingClientRect().width}px`,
                            zIndex: 9999
                          }}
                        >
                          {locationTypes.map(type => (
                            <div key={type.value} className="menu-section">
                              <div 
                                className={`menu-item has-submenu ${openSubmenu === type.value ? 'open' : ''}
                                  ${selectedLocation.info?.type === type.value ? 'has-selection' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenSubmenu(openSubmenu === type.value ? null : type.value);
                                  setOpenStateSubmenu(null);
                                }}
                              >
                                <span>{type.label}</span>
                                <ChevronDown size={16} />
                              </div>
                              {openSubmenu === type.value && (
                                <div className="submenu">
                                  {getLocationOptions(type.value).map(location => {
                                    if (type.value === 'county' && location.hasSubmenu) {
                                      return (
                                        <div key={location.value}>
                                          <div 
                                            className={`menu-item has-submenu ${openStateSubmenu === location.state ? 'open' : ''}
                                              ${selectedLocation.info?.state === location.state && selectedLocation.info?.type === 'county' && 
                                                (selectedLocation.info?.name?.startsWith('All ') || selectedLocation.info?.name?.includes(' County')) ? 'has-selection' : ''}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenStateSubmenu(openStateSubmenu === location.state ? null : location.state);
                                            }}
                                          >
                                            <span>{location.label}</span>
                                            <ChevronDown size={16} />
                                          </div>
                                          {openStateSubmenu === location.state && (
                                            <div className="state-counties-submenu">
                                              {getCountiesForState(location.state).map(county => {
                                                let isSelected = false;
                                                
                                                if (county.isAllStateCounties) {
                                                  const currentName = (selectedLocation.info?.name || '').replace(/[-]/g, '').trim();
                                                  const stateName = county.state;
                                                  const stateAbbr = county.stateAbbr;
                                                  
                                                  isSelected = 
                                                    currentName === `All ${stateName} Counties`.trim() || 
                                                    currentName === `All ${stateAbbr} Counties`.trim() ||
                                                    currentName === `${stateName} Counties`.trim();
                                                } else {
                                                  isSelected = selectedLocation.info?.name === county.fullLabel;
                                                }
                                                
                                                return (
                                                  <div 
                                                    key={county.value} 
                                                    className={`menu-item ${isSelected ? 'disabled' : ''}`}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (!isSelected) {
                                                        handleFilterLocationSelect(county, 'county');
                                                      }
                                                    }}
                                                  >
                                                    {county.isAllStateCounties ? county.label : county.displayLabel}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    }
                                    if (type.value === 'city' && location.hasSubmenu) {
                                      return (
                                        <div key={location.value}>
                                          <div 
                                            className={`menu-item has-submenu ${openStateSubmenu === location.state ? 'open' : ''}
                                              ${selectedLocation.info?.state === location.state && selectedLocation.info?.type === 'city' && 
                                                (selectedLocation.info?.name?.startsWith('All ') || selectedLocation.info?.name?.includes(',')) ? 'has-selection' : ''}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenStateSubmenu(openStateSubmenu === location.state ? null : location.state);
                                            }}
                                          >
                                            <span>{location.label}</span>
                                            <ChevronDown size={16} />
                                          </div>
                                          {openStateSubmenu === location.state && (
                                            <div className="state-cities-submenu">
                                              {getCitiesForState(location.state).map(city => {
                                                let isSelected = false;
                                                
                                                if (city.isAllStateCities) {
                                                  const currentName = (selectedLocation.info?.name || '').replace(/[-]/g, '').trim();
                                                  const stateName = city.state;
                                                  const stateAbbr = city.stateAbbr;
                                                  
                                                  isSelected = 
                                                    currentName === `All ${stateName} Cities`.trim() || 
                                                    currentName === `All ${stateAbbr} Cities`.trim() ||
                                                    currentName === `${stateName} Cities`.trim();
                                                } else {
                                                  isSelected = selectedLocation.info?.name === city.fullLabel;
                                                }
                                                
                                                return (
                                                  <div 
                                                    key={city.value} 
                                                    className={`menu-item ${isSelected ? 'disabled' : ''}`}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (!isSelected) {
                                                        handleFilterLocationSelect(city, 'city');
                                                      }
                                                    }}
                                                  >
                                                    {city.isAllStateCities ? city.label : city.displayLabel}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    }
                                    
                                    return (
                                      <div 
                                        key={location.value} 
                                        className={`menu-item ${
                                          `- ${selectedLocation.info?.name} -` === location.label || 
                                          selectedLocation.info?.name === location.label
                                            ? 'disabled' 
                                            : ''
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!(`- ${selectedLocation.info?.name} -` === location.label || 
                                                selectedLocation.info?.name === location.label)) {
                                            handleFilterLocationSelect(location, type.value);
                                          }
                                        }}
                                      >
                                        {location.label}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>,
                        document.body
                      )}
                    </div>
                  </div>

                  <div className="filter-select-container">
                    <div className="filter-select-label">
                      <div className="top-row">
                        <ShieldAlert size={16} />
                        <span>Crime Type:</span>
                      </div>
                    </div>
                    <div className={`custom-dropdown crime-dropdown ${isSideCrimeTypeOpen ? 'open' : ''}`}>
                      <button 
                        className="dropdown-trigger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsSideCrimeTypeOpen(!isSideCrimeTypeOpen);
                          setIsLocationOpen(false);
                          setIsSideDateRangeOpen(false);
                          setIsSideDataSourceOpen(false);
                          setIsMainCrimeTypeOpen(false);
                          setIsMainDateRangeOpen(false);
                          setIsMainDataSourceOpen(false);
                          setOpenSubmenu(null);
                          setOpenStateSubmenu(null);
                        }}
                      >
                        {getSelectedCrimeLabel()}
                      </button>
                      {isSideCrimeTypeOpen && (
                        <div className="dropdown-menu">
                          <div className="menu-section">
                            <div 
                              key="all_crime"
                              className={`menu-item ${selectedCrime === 'all_crime' ? 'disabled' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (selectedCrime !== 'all_crime') {
                                  handleCrimeTypeSelect('all_crime', true);
                                }
                              }}
                            >
                              All Crime
                            </div>
                          
                            {getCrimeTypes(selectedSource).filter(type => type.value !== 'all_crime').map(type => (
                              <div key={type.value}>
                                <div className="submenu">
                                  <div 
                                    className={`menu-item ${selectedCrime === type.value ? 'disabled' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (selectedCrime !== type.value) {
                                        handleCrimeTypeSelect(type.value, true);
                                      }
                                    }}
                                  >
                                    {type.label}
                                  </div>
                                
                                  {type.subTypes && (
                                    <div className="submenu">
                                      {type.subTypes.map(subType => (
                                        <div 
                                          key={subType.value} 
                                          className={`menu-item ${selectedCrime === subType.value ? 'disabled' : ''}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (selectedCrime !== subType.value) {
                                              handleCrimeTypeSelect(subType.value, true);
                                            }
                                          }}
                                        >
                                          {subType.label}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="filter-select-container">
                    <div className="filter-select-label">
                      <div className="top-row">
                        <Calendar size={16} />
                        <span>Date Range:</span>
                      </div>
                    </div>
                    <div className={`custom-dropdown date-dropdown ${isSideDateRangeOpen ? 'open' : ''}`}>
                      <button 
                        className="dropdown-trigger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsSideDateRangeOpen(!isSideDateRangeOpen);
                          setIsLocationOpen(false);
                          setIsSideCrimeTypeOpen(false);
                          setIsSideDataSourceOpen(false);
                          setIsMainCrimeTypeOpen(false);
                          setIsMainDateRangeOpen(false);
                          setIsMainDataSourceOpen(false);
                          setOpenSubmenu(null);
                          setOpenStateSubmenu(null);
                        }}
                      >
                        {dateRanges.find(range => range.value === String(selectedDateRange))?.label || dateRanges[0].label}
                      </button>
                      {isSideDateRangeOpen && (
                        <div className="dropdown-menu date-range">
                          {dateRanges.map(range => {
                            const yearLocked = isYearLockedForFreePlan(range.value);
                            const isSelected = String(selectedDateRange) === range.value;
                            return (
                              <div
                                key={range.value}
                                className={`menu-item ${isSelected ? 'disabled' : ''}${yearLocked ? ' year-locked-free' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isSelected) {
                                    setSelectedDateRange(range.value);
                                    setIsSideDateRangeOpen(false);
                                  }
                                }}
                              >
                                <span className="filter-year-menu-text">{range.label}</span>
                                {yearLocked && (
                                  <Lock size={14} strokeWidth={2} className="filter-year-lock-icon" aria-hidden />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="filter-select-container">
                    <div className="filter-select-label">
                      <div className="top-row">
                        <Database size={16} />
                        <span>Data Source:</span>
                      </div>
                    </div>
                    <div className={`custom-dropdown source-dropdown ${isSideDataSourceOpen ? 'open' : ''}`}>
                      <button 
                        className="dropdown-trigger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsSideDataSourceOpen(!isSideDataSourceOpen);
                          setIsLocationOpen(false);
                          setIsSideCrimeTypeOpen(false);
                          setIsSideDateRangeOpen(false);
                          setIsMainCrimeTypeOpen(false);
                          setIsMainDateRangeOpen(false);
                          setIsMainDataSourceOpen(false);
                          setOpenSubmenu(null);
                          setOpenStateSubmenu(null);
                        }}
                      >
                        {getDataSources(selectedLocation?.info?.type).find(source => source.value === selectedSource)?.label || getDataSources(selectedLocation?.info?.type)[0].label}
                      </button>
                      {isSideDataSourceOpen && (
                        <div className="dropdown-menu">
                          {getDataSources(selectedLocation?.info?.type).map(source => (
                            <div 
                              key={source.value} 
                              className={`menu-item ${selectedSource === source.value ? 'disabled' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (selectedSource !== source.value) {
                                  setSelectedSource(source.value);
                                  setIsSideDataSourceOpen(false);
                                }
                              }}
                            >
                              {source.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="pin-toggle-container">
                <span>Show filters on screen</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={isPinned === 'pinned'}
                    onChange={handlePinToggle}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="filter-footer">
              <button
                className={`filter-menu-button save ${saveState === 'saved' ? 'saved' : saveState === 'duplicate' ? 'duplicate' : saveState === 'saving' ? 'saving' : ''}`}
                onClick={handleSaveClick}
                disabled={saveState === 'saving' || !selectedLocation?.info?.name}
              >
                {saveState === 'saved' ? (
                  <span>Search Saved!</span>
                ) : saveState === 'duplicate' ? (
                  <>
                    <AlertCircle size={14} />
                    <span>Already Saved</span>
                  </>
                ) : saveState === 'saving' ? (
                  <span>Saving...</span>
                ) : (
                  <span>Save Search</span>
                )}
              </button>
              <button className="filter-menu-button view-results" onClick={onClose}>
                <span>View Results</span>
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {(saveState === 'saved' || saveState === 'duplicate') && createPortal(
        <div className={`filter-save-toast ${saveState}`}>
          {saveState === 'saved' ? (
            <><Check size={14} />Search Saved!</>
          ) : (
            <><AlertCircle size={14} />Already Saved</>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default Filters;